/**
 * Générateur — Contrôler les résultats à l'aide d'ordres de grandeur
 * Attendu ID : 614 | Niveau : 6ème | Domaine : NC
 *
 * Types générés :
 *   - ordremult_qcm_unique          (estimer un produit par arrondi aux entiers)
 *   - ordremult_qcm_multiple        (détecter les résultats aberrants)
 *   - ordremult_texte               (estimer l'aire d'un rectangle via SVG)
 *   - ordremult_relier              (refonte v2 « ordres de grandeur » : estimer
 *                                    ou contrôler des annonces — plus de produits
 *                                    égaux par décalage, hors attendu 614)
 *   - ordremult_tableau_vf          (V/F « peut être vrai / forcément faux »)
 *   - ordremult_texte_trou_drag     (compléter estimations par arrondis)
 *
 * Règles dures de la fiche cluster 8 (section ID 614) :
 *   - Estimations = produit des arrondis à l'entier le plus proche.
 *   - Les aberrations détectées sont TOUJOURS des erreurs de placement de virgule (×10 ou ÷10).
 *   - Relier : 4 paires avec couples d'entiers différents, cibles distinctes.
 *   - V/F : au moins 3 « forcément faux » sur 6 lignes dont l'OG détecte l'erreur.
 *
 * Mécanismes-clés testés :
 *   - Arrondi à l'entier le plus proche
 *   - Contrôle "10 × facteur" / "facteur / 10" comme détecteur d'erreurs
 *   - Commutativité et équivalence par décalages compensés
 *
 * Variantes estimées : > 4000
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 614;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_QCM = 'Estimer un produit décimal par arrondi aux entiers';
const L_QCM_MULTI = 'Détecter les résultats aberrants par ordre de grandeur';
const L_SAISIE_AIRE = 'Estimer l\'aire d\'un rectangle à dimensions décimales';
const L_RELIER = 'Relier des produits égaux (décalages compensés)';
const L_VF = 'Peut être vrai / Forcément faux (contrôle par ordre de grandeur)';
const L_TROU_DRAG = 'Compléter des estimations par arrondi (drag)';
const L_ESTIMER_VIE = 'Estimer dans la vie quotidienne (prix, durée) par arrondi';

// ─── Helpers numériques ───

const decCount = (s) => {
    const [, dec = ''] = String(s).split(',');
    return dec.length;
};

/** Produit exact de deux décimaux en écriture française (multiplication entière).
 *  Valide tant que les facteurs strippés restent ≪ Number.MAX_SAFE_INTEGER (~9×10¹⁵). */
const mulDec = (a, b) => {
    const da = decCount(a);
    const db = decCount(b);
    const ai = parseInt(String(a).replace(/[\s,]/g, ''), 10);
    const bi = parseInt(String(b).replace(/[\s,]/g, ''), 10);
    const prod = ai * bi;
    const totalDec = da + db;
    if (totalDec === 0) return String(prod);
    let p = String(prod);
    while (p.length <= totalDec) p = '0' + p;
    const intPart = p.slice(0, p.length - totalDec);
    const decPart = p.slice(p.length - totalDec);
    const intTrim = intPart.replace(/^0+/, '') || '0';
    const decTrim = decPart.replace(/0+$/, '');
    if (decTrim === '') return intTrim;
    return `${intTrim},${decTrim}`;
};

/** Décale la virgule de k rangs vers la gauche. */
const shiftLeft = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const newDecs = decPart.length + k;
    const totalLen = allDigits.length;
    if (newDecs < totalLen) {
        const newInt = allDigits.slice(0, totalLen - newDecs);
        const newDec = allDigits.slice(totalLen - newDecs);
        const newIntTrim = newInt.replace(/^0+/, '') || '0';
        const newDecTrim = newDec.replace(/0+$/, '');
        if (newDecTrim === '') return newIntTrim;
        return `${newIntTrim},${newDecTrim}`;
    }
    const zeros = '0'.repeat(newDecs - totalLen);
    const rawDec = zeros + allDigits;
    const decTrim = rawDec.replace(/0+$/, '');
    if (decTrim === '') return '0';
    return `0,${decTrim}`;
};

/** Décale la virgule de k rangs vers la DROITE (×10^k). */
const shiftRight = (numStr, k) => {
    const clean = String(numStr).replace(/\s/g, '');
    const [intPart, decPart = ''] = clean.split(',');
    const allDigits = intPart + decPart;
    const oldDecs = decPart.length;
    const newDecs = oldDecs - k;
    if (newDecs >= 0) {
        const newInt = allDigits.slice(0, allDigits.length - newDecs) || '0';
        const newDec = allDigits.slice(allDigits.length - newDecs);
        const newIntTrim = newInt.replace(/^0+/, '') || '0';
        const newDecTrim = newDec.replace(/0+$/, '');
        if (newDecTrim === '') return newIntTrim;
        return `${newIntTrim},${newDecTrim}`;
    }
    const zeros = '0'.repeat(-newDecs);
    const fullInt = allDigits + zeros;
    return fullInt.replace(/^0+/, '') || '0';
};

const sameDecimal = (a, b) => {
    const toNum = (s) => parseFloat(String(s).replace(',', '.'));
    return Math.abs(toNum(a) - toNum(b)) < 1e-12;
};

/**
 * Arrondit un décimal à l'entier le plus proche (string → string).
 * "3,7" → "4" ; "3,4" → "3" ; "7,5" → "8" ; "0,8" → "1" ; "0,4" → "0".
 * Règle : si le premier chiffre décimal est ≥ 5, arrondi par excès.
 */
const arrondiEntier = (s) => {
    const clean = String(s).replace(/\s/g, '');
    const [intPart, decPart = ''] = clean.split(',');
    const firstDec = parseInt(decPart[0] || '0', 10);
    const base = parseInt(intPart, 10);
    return String(firstDec >= 5 ? base + 1 : base);
};

// ─── Pools ───

/**
 * Facteurs décimaux à 1 ou 2 chiffres avant la virgule et 1 décimale non nulle.
 * Conçus pour que l'arrondi à l'entier soit non trivial (pas 5,0 par ex).
 */
const POOL_DECIMAUX = [
    '3,7', '2,9', '4,2', '5,4', '6,1', '7,8', '8,2', '1,9', '3,2', '5,8',
    '4,5', '6,4', '2,3', '7,1', '3,8', '4,9', '6,7', '2,6', '5,3', '8,5',
    '1,3', '9,4', '1,1', '2,1',
];

// ═══════════════════════════════════════════════════════════
// 1. QCM UNIQUE — Estimer par arrondi aux entiers
// ═══════════════════════════════════════════════════════════

const FORMULATIONS_QCM_OG = [
    // Revue 2026-08-21 : « meilleur ordre de grandeur » sans méthode imposée
    // trahissait l'élève quand un distracteur (floor × floor…) était plus
    // proche du produit exact que arrondi × arrondi (12,4 % des tirages).
    (a, b) => `<p>Sans poser le calcul, quel est l'ordre de grandeur de ${a} × ${b}, en arrondissant chaque facteur à l'entier le plus proche ?</p>`,
    (a, b) => `<p>Estime le produit ${a} × ${b} en arrondissant chaque facteur à l'entier le plus proche.</p>`,
    (a, b) => `<p>Parmi ces 4 valeurs, laquelle est l'estimation de ${a} × ${b} par arrondi aux entiers ?</p>`,
];

// Anti-répétition immédiate (revue 2026-08-21, recette standard).
let derniereSignatureQcm614 = null;

const genOrdreMultQcm = () => {
    let payload = construireOrdreMultQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureQcm614; t++) {
        payload = construireOrdreMultQcm();
    }
    derniereSignatureQcm614 = payload.texte;
    return payload;
};

const construireOrdreMultQcm = () => {
    // Piocher 2 décimaux dont le produit arrondi est non nul
    let a, b, arA, arB, correctNum;
    let tries = 0;
    do {
        a = randomChoice(POOL_DECIMAUX);
        b = randomChoice(POOL_DECIMAUX);
        arA = parseInt(arrondiEntier(a), 10);
        arB = parseInt(arrondiEntier(b), 10);
        correctNum = arA * arB;
        tries++;
    } while (correctNum === 0 && tries < 30);

    const correct = String(correctNum);

    // Distracteurs : familles didactiques classiques
    //   D1 : ADDITION au lieu de × (arA + arB)
    //   D2 : OG × 10 (virgule oubliée / virgule mal placée)
    //   D3 : arrondi systématique vers le BAS (floor × floor)
    //   D4 : arrondi systématique vers le HAUT (ceil × ceil)
    //   D5 : voisin (un seul facteur arrondi de travers, ex (arA-1) × arB)
    const floorA = parseInt(String(a).split(',')[0], 10);
    const floorB = parseInt(String(b).split(',')[0], 10);
    const ceilA = floorA + (String(a).includes(',') ? 1 : 0);
    const ceilB = floorB + (String(b).includes(',') ? 1 : 0);
    const dist = new Set();
    dist.add(String(arA + arB));                         // D1 addition
    dist.add(String(correctNum * 10));                    // D2 virgule oubliée
    dist.add(String(floorA * floorB));                    // D3 floor
    dist.add(String(ceilA * ceilB));                      // D4 ceil
    // D5 : voisins avec un seul facteur décalé
    if (arA > 1) dist.add(String((arA - 1) * arB));
    dist.add(String((arA + 1) * arB));
    if (arB > 1) dist.add(String(arA * (arB - 1)));
    dist.add(String(arA * (arB + 1)));

    // Nettoyer : retirer "0" et la bonne réponse, ne garder que des entiers > 0
    const arr = [...dist].filter((x) => x !== correct && x !== '0');
    // Fallback déterministe si pas assez (cas rarissimes)
    let k = 2;
    while (arr.length < 3) {
        const cand = String(correctNum * 10 - k);
        if (cand !== correct && !arr.includes(cand)) arr.push(cand);
        k++;
        if (k > 20) break;
    }

    return makeQcm(
        [correct, ...arr.slice(0, 3)],
        correct,
        randomChoice(FORMULATIONS_QCM_OG)(a, b),
        opts(L_QCM),
    );
};

// ═══════════════════════════════════════════════════════════
// 2. QCM MULTIPLE — Détecter les résultats aberrants
// ═══════════════════════════════════════════════════════════

const genOrdreMultQcmMultiple = () => {
    // Revue 2026-08-21 : quota VARIABLE d'aberrantes (1 à 3, contre 2 constant),
    // direction ×10 OU ÷10 tirée par ligne (100 % « trop grand » auparavant :
    // cocher les deux gros résultats gagnait sans maths), et unicité des
    // couples (a, b) entre les 4 propositions (même produit affiché deux fois
    // dans 28/5000 tirages → l'un se déduisait de l'autre).
    const propositions = [];
    const couplesPris = new Set();
    const nbAberrantes = randInt(1, 3);
    const nbVraies = 4 - nbAberrantes;

    // Lignes cohérentes (le résultat annoncé est le VRAI résultat)
    for (let t = 0; t < 30 && propositions.filter((p) => p.isVrai).length < nbVraies; t++) {
        const a = randomChoice(POOL_DECIMAUX);
        const b = randomChoice(POOL_DECIMAUX);
        const cle = `${a}|${b}`;
        if (couplesPris.has(cle)) continue;
        const vrai = mulDec(a, b);
        couplesPris.add(cle);
        propositions.push({ text: `${a} × ${b} = ${vrai}`, isVrai: true });
    }

    // Lignes aberrantes (×10 ou ÷10, tiré au sort)
    for (let t = 0; t < 30 && propositions.filter((p) => !p.isVrai).length < nbAberrantes; t++) {
        const a = randomChoice(POOL_DECIMAUX);
        const b = randomChoice(POOL_DECIMAUX);
        const cle = `${a}|${b}`;
        if (couplesPris.has(cle)) continue;
        const vrai = mulDec(a, b);
        const faux = randInt(0, 1) ? shiftRight(vrai, 1) : shiftLeft(vrai, 1);
        if (sameDecimal(faux, vrai)) continue;
        couplesPris.add(cle);
        propositions.push({ text: `${a} × ${b} = ${faux}`, isVrai: false });
    }

    // Fallback : compléter si besoin (couples encore libres)
    let safety = 0;
    while (propositions.length < 4 && safety < 30) {
        safety++;
        const a = randomChoice(POOL_DECIMAUX);
        const b = randomChoice(POOL_DECIMAUX);
        const cle = `${a}|${b}`;
        if (couplesPris.has(cle)) continue;
        const vrai = mulDec(a, b);
        couplesPris.add(cle);
        propositions.push({ text: `${a} × ${b} = ${vrai}`, isVrai: true });
    }

    const shuffled = shuffle(propositions);
    const allTexts = shuffled.map((p) => p.text);
    const aberrantes = shuffled.filter((p) => !p.isVrai).map((p) => p.text);

    return makeQcm(
        allTexts,
        aberrantes,
        `<p>Un élève a écrit ces résultats. Lesquels sont <strong>forcément faux</strong> d'après l'ordre de grandeur ? (plusieurs réponses possibles)</p><p class="italic text-slate-400">✏️ Pense à vérifier chaque résultat au brouillon avec un ordre de grandeur.</p>`,
        opts(L_QCM_MULTI),
        true,
    );
};

// ═══════════════════════════════════════════════════════════
// 3. SAISIE AIRE — Estimer l'aire d'un rectangle (SVG inline)
// ═══════════════════════════════════════════════════════════

/**
 * Construit un data URL SVG d'un rectangle avec ses dimensions.
 * Même pattern que gen_612 : SVG string → encodeURIComponent → data URL, puis
 * passé via elements_additionnels avec type: 'image' pour que ImageElementEleve
 * le render via <img> (sinon sanitizeHtml strippe l'inline SVG).
 * Couleurs claires (slate-200) pour être lisibles sur le container dark.
 */
const buildRectDataUrl = (L, l) => {
    const fill = '#fef3c7';
    // Le rectangle est dessiné À L'ÉCHELLE des valeurs tirées : la boîte fixe
    // 180 × 90 précédente CONTREDISAIT l'énoncé (un côté de 1,3 cm dessiné deux
    // fois plus long qu'un côté de 7,1 cm). En 6ᵉ, le support visuel doit
    // confirmer les données, pas désorienter.
    const cmL = parseFloat(String(L).replace(',', '.'));
    const cml = parseFloat(String(l).replace(',', '.'));
    const px = Math.min(200 / cmL, 120 / cml);      // échelle UNIFORME sur les deux côtés
    const w = cmL * px, h = cml * px;
    const x = 62, y = 40;
    const vbW = Math.round(x + w + 30), vbH = Math.round(y + h + 34);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vbW} ${vbH}" width="${vbW}" height="${vbH}">`
        + `<rect x="${x}" y="${y}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" fill="${fill}" stroke="currentColor" stroke-width="2"/>`
        + `<text x="${(x + w / 2).toFixed(1)}" y="${y - 12}" text-anchor="middle" font-size="18" fill="currentColor" font-weight="600">${L} cm</text>`
        + `<text x="${x - 8}" y="${(y + h / 2 + 6).toFixed(1)}" text-anchor="end" font-size="18" fill="currentColor" font-weight="600">${l} cm</text>`
        + '</svg>';
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// Anti-répétition immédiate (revue 2026-08-21, recette standard).
let derniereSignatureAire614 = null;

const genOrdreMultSaisieAire = () => {
    let payload = construireOrdreMultSaisieAire();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureAire614; t++) {
        payload = construireOrdreMultSaisieAire();
    }
    derniereSignatureAire614 = payload.texte;
    return payload;
};

const construireOrdreMultSaisieAire = () => {
    // Choisir L et l dont l'arrondi produit un entier non nul
    let L, l, arL, arl;
    do {
        L = randomChoice(POOL_DECIMAUX);
        l = randomChoice(POOL_DECIMAUX);
        arL = parseInt(arrondiEntier(L), 10);
        arl = parseInt(arrondiEntier(l), 10);
    } while (arL === 0 || arl === 0 || L === l);
    // La « longueur » doit être le plus grand des deux côtés : sinon l'énoncé
    // annonce une longueur inférieure à la largeur, ce que la figure à l'échelle
    // rend immédiatement visible.
    if (parseFloat(L.replace(',', '.')) < parseFloat(l.replace(',', '.'))) {
        [L, l] = [l, L];
        [arL, arl] = [arl, arL];
    }

    const estimation = String(arL * arl);
    const elAdd = {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: buildRectDataUrl(L, l), maxWidth: 'small' } }],
    };

    return makeSaisie(
        estimation,
        `<p>Voici un rectangle de longueur <strong>${L} cm</strong> et de largeur <strong>${l} cm</strong>.</p>{{element:0}}<p>Sans poser le calcul, estime son aire en arrondissant chaque côté à l'entier le plus proche.</p>`,
        {
            ...opts(L_SAISIE_AIRE),
            strict: true,
            suffixe: 'cm²',
            elements_additionnels: elAdd,
        },
    );
};

// ═══════════════════════════════════════════════════════════
// 4. RELIER — Contrôler par ordres de grandeur (refonte v2)
// ═══════════════════════════════════════════════════════════
//
// L'ancienne carte (« produits égaux par décalages compensés ») travaillait
// l'invariance du produit par déplacement de virgule — hors attendu 614.
// v2 (simulateur HTML validé) : DEUX variantes en appariement complet
// (RelierEleve n'accepte pas d'étiquette orpheline) :
//   - « estimer »   : 3 produits de bandes de grandeur éloignées + 1 DUO à
//                     mêmes chiffres (virgule ×10) → relier chaque produit à
//                     « environ E » (E = produit des arrondis à 1 chiffre
//                     significatif) ; le duo oblige à placer la virgule.
//   - « contrôler » : 2 calculs, deux élèves par calcul (résultat EXACT vs
//                     virgule décalée ×10 ou ÷10) → relier chaque annonce au
//                     verdict « ≈ f1 × f2 = E → vraisemblable / impossible ! » ;
//                     les facteurs arrondis ne départagent pas les jumeaux,
//                     seul le contrôle annonce ↔ ordre de grandeur tranche.
// Bandes disjointes (rapport ≥ 2,2 entre étiquettes hors duo, ×10 dans le
// duo) : jamais deux étiquettes hésitables dans une même liste.

/** Pool par bandes de grandeur. f1/f2 = facteurs arrondis à 1 chiffre
 *  significatif (nombres simples de 6e), e = estimation f1 × f2 (affichage). */
const BANDES_OG = [
    [{ a: '1,9', b: '6,3', f1: '2', f2: '6', e: '12' },
        { a: '5,2', b: '1,8', f1: '5', f2: '2', e: '10' },
        { a: '3,1', b: '2,9', f1: '3', f2: '3', e: '9' }],
    [{ a: '4,2', b: '5,8', f1: '4', f2: '6', e: '24' },
        { a: '8,1', b: '3,9', f1: '8', f2: '4', e: '32' },
        { a: '9,7', b: '3,2', f1: '10', f2: '3', e: '30' }],
    [{ a: '19,6', b: '3,1', f1: '20', f2: '3', e: '60' },
        { a: '8,9', b: '8,2', f1: '9', f2: '8', e: '72' }],
    [{ a: '58,6', b: '3,1', f1: '60', f2: '3', e: '180' },
        { a: '41,2', b: '4,9', f1: '40', f2: '5', e: '200' },
        { a: '39', b: '6,2', f1: '40', f2: '6', e: '240' }],
    [{ a: '9,8', b: '51,3', f1: '10', f2: '50', e: '500' },
        { a: '61,8', b: '9,9', f1: '60', f2: '10', e: '600' },
        { a: '98,7', b: '7,2', f1: '100', f2: '7', e: '700' }],
    [{ a: '49,2', b: '41', f1: '50', f2: '40', e: '2 000' },
        { a: '68,7', b: '29,4', f1: '70', f2: '30', e: '2 100' }],
];

const PRENOMS_OG = ['Lina', 'Tom', 'Zoé', 'Noé', 'Mila', 'Sacha', 'Inès', 'Léo'];

const ogNum = (s) => parseFloat(String(s).replace(/\s/g, '').replace(',', '.'));
const ogProduit = (l) => `${l.a} × ${l.b}`;

/** Espace des milliers sur la partie entière (« 20172,4 » → « 20 172,4 »). */
const ogMilliers = (s) => {
    const [ent, dec] = String(s).replace(/\s/g, '').split(',');
    const grp = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return dec === undefined ? grp : `${grp},${dec}`;
};

/** Version « virgule décalée » (×10 sur le facteur décimal → mêmes chiffres). */
const ogDecale = (l) => (l.a.includes(',')
    ? { ...l, a: shiftRight(l.a, 1), f1: shiftRight(l.f1, 1), e: ogMilliers(mulDec(l.e, '10')) }
    : { ...l, b: shiftRight(l.b, 1), f2: shiftRight(l.f2, 1), e: ogMilliers(mulDec(l.e, '10')) });

/** 3 lignes issues de 3 bandes distinctes (une ligne par bande). */
const ogTirerLignes = (nb) => shuffle(BANDES_OG.map((_, i) => i)).slice(0, nb)
    .map((i) => randomChoice(BANDES_OG[i]));

const genOrdreMultRelierEstimer = () => {
    for (let essai = 0; essai < 80; essai++) {
        const lignes = ogTirerLignes(3);
        const base = randomChoice(lignes);
        const toutes = shuffle([...lignes, ogDecale(base)]);
        const cles = toutes.map((l) => ogNum(l.e));
        let ok = new Set(cles).size === 4;
        for (let i = 0; ok && i < 4; i++) {
            for (let j = i + 1; ok && j < 4; j++) {
                const r = Math.max(cles[i], cles[j]) / Math.min(cles[i], cles[j]);
                if (r < 2.2) ok = false;
            }
        }
        if (!ok) continue;
        return makeRelier(
            toutes.map((l) => ({ source: ogProduit(l), target: `environ ${l.e}` })),
            '<p><b>Sans poser les calculs</b>, relie chaque produit à son <b>ordre de grandeur</b> : '
            + 'arrondis chaque facteur à un nombre simple.</p>'
            + '<p><b>Attention :</b> deux produits ont les <b>mêmes chiffres</b> — place bien la virgule !</p>',
            opts(L_RELIER),
        );
    }
    // Repli déterministe (duo 4,2 × 5,8 / 42 × 5,8)
    const l0 = BANDES_OG[1][0];
    return makeRelier(
        [BANDES_OG[0][0], l0, ogDecale(l0), BANDES_OG[4][0]]
            .map((l) => ({ source: ogProduit(l), target: `environ ${l.e}` })),
        '<p><b>Sans poser les calculs</b>, relie chaque produit à son <b>ordre de grandeur</b> : '
        + 'arrondis chaque facteur à un nombre simple.</p>'
        + '<p><b>Attention :</b> deux produits ont les <b>mêmes chiffres</b> — place bien la virgule !</p>',
        opts(L_RELIER),
    );
};

const genOrdreMultRelierControler = () => {
    for (let essai = 0; essai < 80; essai++) {
        const [b1, b2] = shuffle(BANDES_OG.map((_, i) => i)).slice(0, 2);
        const lig = [randomChoice(BANDES_OG[b1]), randomChoice(BANDES_OG[b2])];
        // bandes suffisamment éloignées (verdicts jumeaux : la distance joue entre duos)
        const [e1, e2] = lig.map((l) => ogNum(l.e));
        if (Math.max(e1, e2) / Math.min(e1, e2) < 2.2) continue;
        const prenoms = shuffle(PRENOMS_OG).slice(0, 4);
        const pairs = [];
        lig.forEach((l, k) => {
            const exact = mulDec(l.a, l.b);
            const annonceFausse = randInt(0, 1) ? shiftRight(exact, 1) : shiftLeft(exact, 1);
            const duo = shuffle([
                { annonce: exact, verdict: 'vraisemblable' },
                { annonce: annonceFausse, verdict: 'impossible !' },
            ]);
            duo.forEach((d, j) => pairs.push({
                source: `${prenoms[2 * k + j]} : ${ogProduit(l)} = ${ogMilliers(d.annonce)}`,
                target: `≈ ${l.f1} × ${l.f2} = ${l.e} → ${d.verdict}`,
            }));
        });
        return makeRelier(
            pairs,
            '<p>Quatre élèves annoncent leur résultat — deux par calcul, et <b>dans chaque duo, '
            + 'quelqu\'un a décalé la virgule !</b></p>'
            + '<p><b>Sans poser les calculs</b>, relie chaque annonce au bon verdict : les facteurs '
            + 'arrondis ne suffisent pas, compare chaque résultat annoncé à l\'ordre de grandeur.</p>',
            opts(L_RELIER),
        );
    }
    return genOrdreMultRelierEstimer();
};

const genOrdreMultRelier = () => (randInt(0, 1)
    ? genOrdreMultRelierEstimer()
    : genOrdreMultRelierControler());

// ═══════════════════════════════════════════════════════════
// 5. TABLEAU V/F — Peut être vrai / Forcément faux
// ═══════════════════════════════════════════════════════════

const genOrdreMultVF = () => {
    const lignes = [];
    const bonnes = []; // 0 = "Peut être vrai", 1 = "Forcément faux"
    const seen = new Set();
    const couplesPris = new Set();

    // Quota variable (revue 2026-08-21) : 3 OU 4 aberrantes — la fiche impose
    // « au moins 3 forcément faux sur 6 », le tirage 3/4 casse quand même le
    // comptage « toujours 3/3 ». Unicité des couples (a, b) entre lignes
    // (82/5000 tirages montraient deux fois le même produit).
    const nbAberrantes = randInt(3, 4);
    const nbCoherentes = 6 - nbAberrantes;

    const push = (a, b, text, isCoherent) => {
        const cle = `${a}|${b}`;
        if (seen.has(text) || couplesPris.has(cle)) return false;
        seen.add(text);
        couplesPris.add(cle);
        lignes.push(text);
        bonnes.push(isCoherent ? 0 : 1);
        return true;
    };

    // Lignes COHÉRENTES (le résultat exact)
    for (let t = 0; t < 30 && lignes.filter((_, i) => bonnes[i] === 0).length < nbCoherentes; t++) {
        const a = randomChoice(POOL_DECIMAUX);
        const b = randomChoice(POOL_DECIMAUX);
        const vrai = mulDec(a, b);
        push(a, b, `${a} × ${b} = ${vrai}`, true);
    }

    // Lignes ABERRANTES (×10 ou ÷10)
    for (let t = 0; t < 40 && lignes.filter((_, i) => bonnes[i] === 1).length < nbAberrantes; t++) {
        const a = randomChoice(POOL_DECIMAUX);
        const b = randomChoice(POOL_DECIMAUX);
        const vrai = mulDec(a, b);
        const direction = Math.random() < 0.5 ? 1 : -1;
        const faux = direction > 0 ? shiftRight(vrai, 1) : shiftLeft(vrai, 1);
        if (sameDecimal(faux, vrai)) continue;
        push(a, b, `${a} × ${b} = ${faux}`, false);
    }

    // Équilibrer si besoin
    let garde = 0;
    while (lignes.length < 6 && garde < 40) {
        garde++;
        const a = randomChoice(POOL_DECIMAUX);
        const b = randomChoice(POOL_DECIMAUX);
        const vrai = mulDec(a, b);
        const cohCount = bonnes.filter((x) => x === 0).length;
        if (cohCount < nbCoherentes) {
            push(a, b, `${a} × ${b} = ${vrai}`, true);
        } else {
            push(a, b, `${a} × ${b} = ${shiftRight(vrai, 1)}`, false);
        }
    }

    // Mélanger
    const indexed = lignes.map((l, i) => ({ l, b: bonnes[i] }));
    const shuf = shuffle(indexed);

    return makeVraiFaux(
        shuf.map((x) => x.l),
        shuf.map((x) => x.b),
        `<p>Un élève a fait ces calculs. Pour chaque résultat, utilise l'ordre de grandeur pour dire s'il peut être vrai ou s'il est forcément faux.</p><p class="italic text-slate-400">✏️ Pense à vérifier chaque résultat au brouillon avec un ordre de grandeur.</p>`,
        { ...opts(L_VF), colonnes: ['Peut être vrai', 'Forcément faux'] },
    );
};

// ═══════════════════════════════════════════════════════════
// 6. TEXTE À TROUS (DRAG) — Compléter estimations par arrondi
// ═══════════════════════════════════════════════════════════

const genOrdreMultTrouDrag = () => {
    const items = [];
    const usedSources = new Set();
    const usedResults = new Set();

    // 3 estimations
    let safety = 0;
    while (items.length < 3 && safety < 50) {
        safety++;
        const a = randomChoice(POOL_DECIMAUX);
        const b = randomChoice(POOL_DECIMAUX);
        const src = `${a} × ${b}`;
        if (usedSources.has(src)) continue;
        const arA = parseInt(arrondiEntier(a), 10);
        const arB = parseInt(arrondiEntier(b), 10);
        const est = String(arA * arB);
        if (est === '0') continue;
        if (usedResults.has(est)) continue;
        usedSources.add(src);
        usedResults.add(est);
        items.push({ a, b, est });
    }

    // Fallback
    if (items.length < 3) {
        items.length = 0;
        usedResults.clear();
        items.push({ a: '4,2', b: '2,9', est: '12' });
        items.push({ a: '7,8', b: '1,1', est: '8'  });
        items.push({ a: '6,4', b: '5,2', est: '30' });
    }

    const phrase = items.map((it) => `${it.a} × ${it.b} ≈ {{${it.est}}}`).join('\n');

    // Distracteurs : chaque bonne × 10 (virgule oubliée)
    const dist = new Set();
    const bonnesSet = new Set(items.map((i) => i.est));
    for (const it of items) {
        const d = String(parseInt(it.est, 10) * 10);
        if (!bonnesSet.has(d) && !dist.has(d)) dist.add(d);
    }
    // Compléter si besoin
    let safety2 = 0;
    while (dist.size < 3 && safety2 < 20) {
        safety2++;
        const it = randomChoice(items);
        const d = String(parseInt(it.est, 10) * 100);
        if (!bonnesSet.has(d) && !dist.has(d)) dist.add(d);
        else safety2++;
    }

    return makeTexteTrou(
        phrase,
        `<p>Complète chaque estimation en faisant glisser le bon ordre de grandeur (produit des arrondis aux entiers).</p>`,
        { ...opts(L_TROU_DRAG), mode: 'drag', distracteurs: [...dist].slice(0, 3), strict: false },
    );
};

// ═══════════════════════════════════════════════════════════
// 7. ESTIMER DANS LA VIE QUOTIDIENNE — Saisie contextualisée
// ═══════════════════════════════════════════════════════════
//
// Deux contextes tirés aléatoirement : prix total (× quantité) et durée
// totale (× nombre d'occurrences). Dans les deux cas, l'élève arrondit le
// facteur décimal à l'entier le plus proche, multiplie par le nombre entier,
// et donne l'estimation avec l'unité correspondante (€ ou min).
//
// Pools restreints aux valeurs qui s'arrondissent à un entier ≥ 1 (sinon
// l'estimation donnerait 0 et perdrait son sens).

const POOL_PRIX_OBJETS = [
    { sing: 'carnet',           plu: 'carnets',           det: 'un' },
    { sing: 'livre',            plu: 'livres',            det: 'un' },
    { sing: 'cahier',           plu: 'cahiers',           det: 'un' },
    { sing: 'jouet',            plu: 'jouets',            det: 'un' },
    { sing: 'jeu de cartes',    plu: 'jeux de cartes',    det: 'un' },
    { sing: 'BD',               plu: 'BD',                det: 'une' },
    { sing: 'figurine',         plu: 'figurines',         det: 'une' },
    { sing: 'boîte de crayons', plu: 'boîtes de crayons', det: 'une' },
    { sing: 'règle',            plu: 'règles',            det: 'une' },
    { sing: 'gomme',            plu: 'gommes',            det: 'une' },
];

const POOL_PRIX_VALEURS = [
    '1,20', '1,35', '1,50', '1,75', '1,85', '1,95',
    '2,10', '2,35', '2,50', '2,75', '2,90',
    '3,15', '3,45', '3,70', '3,95',
    '4,20', '4,50', '4,85',
    '5,30', '5,75',
    '6,10', '6,55',
    '7,40', '7,90',
];

const POOL_DUREE_ACTIVITES = [
    { sing: 'vidéo',         plu: 'vidéos',         det: 'une' },
    { sing: 'épisode',       plu: 'épisodes',       det: 'un' },
    { sing: 'chanson',       plu: 'chansons',       det: 'une' },
    { sing: 'publicité',     plu: 'publicités',     det: 'une' },
    { sing: 'sketch',        plu: 'sketches',       det: 'un' },
    { sing: 'dessin animé',  plu: 'dessins animés', det: 'un' },
    { sing: 'reportage',     plu: 'reportages',     det: 'un' },
    { sing: 'tutoriel',      plu: 'tutoriels',      det: 'un' },
];

const POOL_DUREE_VALEURS = [
    '1,4', '1,8', '2,3', '2,7', '3,2', '3,6', '3,9',
    '4,2', '4,7', '5,3', '5,8', '6,4', '6,9',
    '7,3', '7,8', '8,4',
];

/** Capitalise la première lettre d'une chaîne. */
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const genEstimerVieQuotidienSaisie = () => {
    const isPrix = Math.random() < 0.5;

    if (isPrix) {
        const obj = randomChoice(POOL_PRIX_OBJETS);
        const prix = randomChoice(POOL_PRIX_VALEURS);
        const quantite = randInt(3, 9);
        const prixArrondi = parseInt(arrondiEntier(prix), 10);
        const estimation = String(prixArrondi * quantite);

        const texte = `<p>${cap(obj.det)} ${obj.sing} coûte <strong>${prix} €</strong>.</p><p>Estime le prix de <strong>${quantite} ${obj.plu}</strong> en arrondissant à l'euro le plus proche.</p>`;

        return makeSaisie(estimation, texte, {
            ...opts(L_ESTIMER_VIE),
            strict: true,
            suffixe: '€',
        });
    }

    // Durée
    const act = randomChoice(POOL_DUREE_ACTIVITES);
    const duree = randomChoice(POOL_DUREE_VALEURS);
    const nombre = randInt(3, 9);
    const dureeArrondie = parseInt(arrondiEntier(duree), 10);
    const estimation = String(dureeArrondie * nombre);

    const texte = `<p>${cap(act.det)} ${act.sing} dure <strong>${duree} min</strong>.</p><p>Estime la durée totale de <strong>${nombre} ${act.plu}</strong> en arrondissant à la minute la plus proche.</p>`;

    return makeSaisie(estimation, texte, {
        ...opts(L_ESTIMER_VIE),
        strict: true,
        suffixe: 'min',
    });
};

// ─── Registre ───
const GENERATORS = {
    // Cœur : estimer un produit par arrondi
    ordremult_qcm_unique: genOrdreMultQcm,
    ordremult_relier: genOrdreMultRelier,
    ordremult_texte_trou_drag: genOrdreMultTrouDrag,
    // Contexte : contrôler les résultats d'un élève (détection d'erreurs)
    controle_eleve_qcm_multiple: genOrdreMultQcmMultiple,
    controle_eleve_tableau_vf: genOrdreMultVF,
    // Contexte : estimer l'aire d'un rectangle (figure SVG)
    estimer_aire_texte: genOrdreMultSaisieAire,
    // Contexte : estimer dans la vie quotidienne (prix ou durée)
    estimer_quotidien_texte: genEstimerVieQuotidienSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'ordremult', label: 'Estimer un produit par arrondi aux entiers', formatsList: buildFormats('ordremult') },
    { id: 'controle_eleve', label: "Contrôler les résultats d'un élève par ordre de grandeur", formatsList: buildFormats('controle_eleve') },
    { id: 'estimer_aire', label: "Estimer l'aire d'un rectangle par arrondi", formatsList: buildFormats('estimer_aire') },
    { id: 'estimer_quotidien', label: 'Estimer dans la vie quotidienne (prix, durée)', formatsList: buildFormats('estimer_quotidien') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
